import React from "react";
// Meter Command report component

// Default
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import "moment-timezone";

// Mui
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid2";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";

// Custom
import ContainedButton from "../../forms/button/ContainedButton";
import CustomSearch from "../search/Search";
import CustomDataTable from "../table/CustomTable";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import ReadClusterLineChart from "../charts/ReadClusterLineChart";
import CustomToast from "../toast/CustomToast";
import LottieLoader from "../animatedLoader/LottieLoader";

// Svg
import Calendar2 from "../../assets/svg/calendar2";
import Download from "../../assets/svg/download";
import Share from "../../assets/svg/share";
import DownArrow from "../../assets/svg/downArrow";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatTime, formatDate } from "../../utils/helper";
import CustomInputLabelSelect from "../../forms/select/InputLabelCustomSelect";
import useReadCluster from "../../hooks/useReadCluster";
import { ToastState } from "../../types";

// Types
interface LiveDataRecord {
  currentDateTime: string;
  currentRead: string | number;
  currentReadQuality: string;
  readId: string;
}

interface ChartData {
  xAxis: string[];
  yAxis: (string | number)[];
  legend: string;
}

interface SelectOption {
  value: string;
  type: string;
}

const OPTIONS: SelectOption[] = [
  { type: "MeterTime", value: "IP_MeterTime" },
  { type: "Current", value: "IP_Current" },
  { type: "Voltage", value: "IP_Voltage" },
  { type: "Frequency", value: "IP_Frequency" },
  { type: "NeutralCurrent", value: "IP_NeutralCurrent" },
  { type: "PowerFactor", value: "IP_PowerFactor" },
  { type: "kW", value: "IP_KW" },
  { type: "kVA", value: "IP_KVA" },
  { type: "kWh", value: "IP_KWH" },
  { type: "kWhEXP", value: "IP_KWHEXP" },
  { type: "kVAh", value: "IP_KVAH" },
  { type: "kVAhEXP", value: "IP_KVAHEXP" },
  { type: "MDkW", value: "IP_MDKW" },
  { type: "MDkVA", value: "IP_MDKVA" },
  { type: "MDResetCount", value: "IP_MDResetCount" },
  { type: "ProgramCount", value: "IP_ProgramCount" },
  { type: "TamperCount", value: "IP_TamperCount" },
  { type: "TotalPowerOnDuration", value: "IP_TotalPowerOnDuration" },
  { type: "RelayStatus", value: "IP_RelayStatus" },
  { type: "LoadLimitValue", value: "IP_LoadLimitValue" },
];

const COLUMNS = ["Read Date/Time", "Read Value", "VEE Status"];

const CHIP_COLORS = {
  good: "#34C759",
  pending: "#FF9500",
  failed: "#F65B5B",
};

const startOfDayIST = moment().tz("Asia/Kolkata").startOf("day").valueOf();
const endOfDayIST = moment().tz("Asia/Kolkata").endOf("day").valueOf();

const getTimeStamp = (newTime: string, newDate: Date): number => {
  const [hours, minutes] = newTime.split(":").map(Number);

  // Create a moment object in IST using newDate
  const istMoment = moment
    .tz(newDate, "Asia/Kolkata")
    .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

  return istMoment.valueOf(); // Returns UTC timestamp representing the selection in IST
};

const MeterCommandReport: React.FC = () => {
  const [registerType, setRegisterType] = useState<SelectOption>({
    value: "",
    type: "",
  });
  const [page, setPage] = useState(1);
  const [meterId, setMeterId] = useState<string>("");
  // a9177a70-11ac-11ef-a48b-f7c03fec7f37
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LiveDataRecord[]>([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [timeStamp, setTimeStamp] = useState({
    start: startOfDayIST,
    end: endOfDayIST,
  });
  const [chartData, setChartData] = useState<ChartData>({
    xAxis: [],
    yAxis: [],
    legend: "",
  });
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  // const [isLoading, setIsLoading] = useState(false);
  const [downloadEl, setDownloadEl] = useState<HTMLElement | null>(null);
  const [csvLoader, setCsvLoader] = useState(false);
  const [excelLoader, setExcelLoader] = useState(false);

  const location = useLocation();

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);

  const handleDateSubmit = () => {
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));

    console.log(timeRange, startTs, endTs);

    setTimeStamp({
      start: startTs,
      end: endTs,
    });

    setAnchorEl(null);
  };

  useEffect(() => {
    if (location.state) {
      const { startDate, endDate, searchValue, registerTypeCode } =
        location.state as {
          startDate: string;
          endDate: string;
          searchValue: string;
          registerTypeCode: { label: string; value: string };
        };
      const updatedType: SelectOption = {
        type: registerTypeCode?.label,
        value: registerTypeCode?.value,
      };
      setTimeStamp({
        start: moment(startDate).valueOf(),
        end: moment(endDate).valueOf(),
      });
      setMeterId(searchValue);
      setRegisterType(updatedType);
    }
  }, [location]);

  const {
    data: apiData,
    isLoading,
    error,
    refetch,
  } = useReadCluster({
    meterId,
    registerType: {
      value: registerType?.value || "",
      type: registerType?.type || "",
    },
    timeStamp,
    rowsPerPage,
  });

  useEffect(() => {
    console.log("error", error);

    if (error) {
      setToast({
        message: "No data found.",
        severity: "error",
      });
    }
  }, [error]);

  useEffect(() => {
    if (apiData && apiData?.chartData) {
      setData(apiData?.transformedLiveData || []);
      setChartData(apiData?.chartData || { xAxis: [], yAxis: [], legend: "" });
    } else {
      setData([]);
      setChartData({ xAxis: [], yAxis: [], legend: "" });
    }
  }, [apiData]);

  console.log(apiData, "apidata----");

  // const searchReadCluster = async () => {
  //   if (meterId) {
  //     try {
  //       setIsLoading(true);
  //       const response = await fetchGETRequest(
  //         `plugins/telemetry/DEVICE/${meterId}/values/timeseries?keys=${registerType?.value}&startTs=${timeStamp?.start}&endTs=${timeStamp?.end}&limit=${rowsPerPage}`
  //       );

  //       const livedata = response;

  //       if (Object.keys(response).length === 0) {
  //         setData([]);
  //         setChartData({
  //           xAxis: [],
  //           yAxis: [],
  //           legend: null,
  //         });
  //         setToast({
  //           message: `No data found.`,
  //           severity: "error",
  //         });
  //         return;
  //       }

  //       const filteredData = livedata[registerType?.value] || [];

  //       const transformedLiveData = filteredData?.map((record) => ({
  //         currentDateTime: `${moment
  //           .utc(record.ts)
  //           .format("YYYY-MM-DDTHH:mm:ss")}Z`,
  //         currentRead: record.value,
  //         currentReadQuality: "Good",
  //         readId: registerType?.value,
  //       }));

  //       setData(transformedLiveData);
  //       const sortedData = filteredData
  //         ?.sort((a, b) => new Date(a.ts) - new Date(b.ts))
  //         ?.map((record) => ({
  //           currentDateTime: moment
  //             .utc(record.ts)
  //             .format("YYYY-MM-DD HH:mm:ss"),
  //           currentRead: record.value,
  //           currentReadQuality: "Good",
  //         }));

  //       setChartData({
  //         xAxis: sortedData?.map((item) => item?.currentDateTime),
  //         yAxis: sortedData?.map((item) => item?.currentRead),
  //         legend: registerType?.type,
  //       });
  //     } catch (error) {
  //       return error;
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   } else {
  //     setData([]);
  //     setChartData({
  //       xAxis: [],
  //       yAxis: [],
  //       legend: null,
  //     });
  //   }
  // };

  useEffect(() => {
    if (meterId) {
      refetch();
    }
  }, [rowsPerPage, refetch, meterId]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const getStatusChip = (type: string) => {
    return (
      <Box
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          lineHeight: "17px",
          letterSpacing: "0.32px",
          padding: "1px 8px",
          color: "#ffffff",
          borderRadius: "3px",
          width: "fit-content",
          background:
            type === "Failed kWh threshold validation"
              ? "#F65B5B"
              : CHIP_COLORS[type.toLowerCase() as keyof typeof CHIP_COLORS],
        }}
      >
        {type}
      </Box>
    );
  };

  const downloadCSV = async () => {
    if (meterId) {
      try {
        setCsvLoader(true);
        const response = await fetchGETRequest(
          `plugins/telemetry/DEVICE/${meterId}/values/timeseries?keys=IP_KWH,IP_MeterTime,IP_Current,IP_Voltage,IP_Frequency,IP_NeutralCurrent,IP_PowerFactor,IP_KW,IP_KVA,IP_KWHEXP,IP_KVAH,IP_KVAHEXP,IP_MDKW,IP_MDKVA,IP_MDResetCount,IP_ProgramCount,IP_TamperCount,IP_TotalPowerOnDuration,IP_RelayStatus,IP_LoadLimitValue,EventCode,EventDescription&startTs=${timeStamp?.start}&endTs=${timeStamp?.end}&intervalType=WEEK`,
          {},
          true,
        );

        const meterData = await fetchGETRequest(`device/${meterId}`);

        if (response?.data && Object.keys(response?.data).length !== 0) {
          const keys = Object.keys(response?.data);
          const maxRows = Math.max(
            ...Object.values(response?.data).map((arr: any) => arr.length),
          );

          const tableData = Array.from({ length: maxRows }).map(
            (_, rowIndex) => {
              let rowObject: any = {
                meter: meterData?.name ? meterData?.name : meterId,
                timestamp:
                  formatDate(
                    new Date(response?.data[keys[0]][rowIndex]?.ts),
                    "DD/MM/YYYY HH:mm:ss",
                  ) || "N/A",
              };
              keys.forEach((key) => {
                rowObject[key.replace("IP_", "")] =
                  response?.data[key][rowIndex]?.value || "N/A";
              });
              return rowObject;
            },
          );

          const headers = Object.keys(tableData[0]).join(",");
          const rows = tableData
            .map((row) => Object.values(row).join(","))
            .join("\n");
          const csvContent = `${headers}\n${rows}`;

          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
          });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `${window.location.pathname.slice(1)}-${meterId}.csv`;
          link.click();

          URL.revokeObjectURL(link.href);
        } else {
          setToast({
            message: `No data found for meter: ${meterId}`,
            severity: "error",
          });
        }
      } catch (error) {
        return error;
      } finally {
        setCsvLoader(false);
        setDownloadEl(null);
      }
    } else {
      setToast({
        message: "Please enter meter Id",
        severity: "error",
      });
    }
  };

  const downloadExcel = async () => {
    try {
      if (meterId && registerType) {
        setExcelLoader(true);
        const response = await fetchGETRequest(
          `plugins/telemetry/DEVICE/${meterId}/values/timeseries?keys=${registerType?.value}&startTs=${timeStamp?.start}&endTs=${timeStamp?.end}&limit=${rowsPerPage}`,
        );

        const livedata = response;

        if (Object.keys(response).length === 0) {
          setToast({
            message: `No data found.`,
            severity: "error",
          });
          return;
        }

        const filteredData = livedata[registerType?.value] || [];

        const transformedLiveData = filteredData?.map((record: any) => ({
          currentDateTime: formatDate(
            new Date(record.ts),
            "DD/MM/YYYY HH:mm:ss",
          ),
          currentRead: record.value,
          currentReadQuality: "Good",
          readId: registerType?.value,
        }));

        const headers = Object.keys(transformedLiveData[0]).join("\t");
        const rows = transformedLiveData
          .map((row: any) => Object.values(row).join("\t"))
          .join("\n");
        const excelContent = `${headers}\n${rows}`;

        const blob = new Blob([excelContent], {
          type: "application/vnd.ms-excel;charset=utf-8;",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${registerType?.type}.xls`;
        link.click();

        URL.revokeObjectURL(link.href);
      } else {
        setToast({
          message: "Please enter meter Id",
          severity: "error",
        });
      }
    } catch (error) {
      return error;
    } finally {
      setExcelLoader(false);
      setDownloadEl(null);
    }
  };

  const rowsContent = data?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.currentDateTime}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.currentRead}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {Number(item.currentRead) > 500
            ? getStatusChip("Failed kWh threshold validation")
            : getStatusChip(item.currentReadQuality)}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Box>
      <CustomToast
        open={toast.message}
        severity={toast.severity}
        setToast={setToast}
      />

      <Card className="meter-command_report_top_card">
        <Stack direction={"row"} alignItems={"center"}>
          <Stack>
            {/* <CustomSelect
              value={registerType}
              onChange={(e) => setRegisterType(e.target.value)}
              icon={DownArrow}
              title={"Register Type"}
              options={OPTIONS}
              titleSx={{
                fontSize: "17px",
                lineHeight: "20px",
              }}
              iconSx={{
                marginRight: "5px",
                marginTop: "5px",
              }}
              showSelected={false}
            /> */}

            <CustomInputLabelSelect
              value={registerType}
              onChange={(e) => {
                setRegisterType({
                  value: e.value as string,
                  type: e.type as string,
                });
              }}
              icon={DownArrow}
              title={"Register Type"}
              options={OPTIONS}
              titleSx={{
                fontSize: "14px",
                lineHeight: "20px",
              }}
              iconSx={{
                marginRight: "5px",
                marginTop: "5px",
              }}
            />
            {/* <Typography
              sx={{
                fontSize: "12px",
                lineHeight: "19px",
                fontWeight: 400,
                color: "#A1A9C5",
              }}
            >
              {registerType?.type}
            </Typography> */}
          </Stack>
        </Stack>
        <Stack direction={"row"} alignItems={"center"}>
          <Box mr={"20px"}>
            <CustomSearch
              value={meterId}
              onChange={(e) => setMeterId(e.target.value)}
              className={"meter-command_report_top_card_search"}
              placeholder={"Enter Meter Id"}
              isIcon={false}
            />
          </Box>
          <Box
            sx={{
              fontWeight: 600,
              letterSpacing: "0.32px",
              padding: "1px 8px",
              color: "#ffffff",
              width: "fit-content",
            }}
          >
            <Stack
              direction={"row"}
              justifyContent={"center"}
              gap={"5px"}
              alignItems={"center"}
              px={"5px"}
            >
              <Typography>{formatDate(startDate, "D MMMM YYYY")} - </Typography>
              <Typography>{formatDate(endDate, "D MMMM YYYY")}</Typography>
            </Stack>
          </Box>
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
            <Calendar2 />
          </IconButton>
          <Box
            sx={{
              width: "1px",
              height: "31px",
              background: "#CACDD7",
              margin: "0px 20px",
            }}
          ></Box>
          <ContainedButton
            disabled={!startDate || !endDate || !registerType || !meterId}
            label={"Apply Filter"}
            sx={{
              color: "#ffffff",
              width: "fit-content",
              boxShadow: "0",
            }}
            onClick={() => refetch()}
            loading={isLoading}
          />
          <Stack direction={"row"} gap={"20px"} ml={"34px"}>
            <IconButton onClick={(event) => setDownloadEl(event.currentTarget)}>
              <Download />
            </IconButton>
            <Share />

            <Popover
              open={!!downloadEl}
              anchorEl={downloadEl}
              onClose={() => setDownloadEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              {csvLoader ? (
                <Stack sx={{ p: 2 }}>
                  <LottieLoader />
                </Stack>
              ) : (
                <IconButton onClick={downloadCSV}>
                  <Typography sx={{ p: 2 }}>Csv</Typography>
                </IconButton>
              )}
              <Divider />
              {excelLoader ? (
                <Stack sx={{ p: 2 }}>
                  <LottieLoader />
                </Stack>
              ) : (
                <IconButton onClick={downloadExcel}>
                  <Typography sx={{ p: 2 }}>Excel</Typography>
                </IconButton>
              )}
            </Popover>
          </Stack>
        </Stack>
      </Card>

      <Grid container spacing={2.25} sx={{ marginTop: "20px" }}>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Card
            sx={{ padding: "20px", maxHeight: "640px", minHeight: "203px" }}
          >
            <CustomDataTable
              sx={{ maxHeight: "600px", bgColor: "#1976d2 !important" }}
              columns={COLUMNS}
              // totalCount={200}
              isLoading={loading}
              pagination={data?.length > 0}
              rowsPerPage={rowsPerPage}
              page={page}
              rowsContent={rowsContent}
              rowsPerPageOptions={[10, 50, 100, 250, 500, 1000, 1500]}
              // onPageChange={handlePageChange}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Card
            sx={{
              padding: "20px",
              height: `${
                chartData?.xAxis?.length === 0 && chartData?.yAxis?.length === 0
                  ? "203px"
                  : "640px"
              }`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {chartData?.xAxis?.length === 0 &&
            chartData?.yAxis?.length === 0 ? (
              <Typography variant="h6">No chart found</Typography>
            ) : (
              <ReadClusterLineChart
                xAxisData={chartData.xAxis}
                yAxisData={chartData?.yAxis}
                legend={chartData?.legend}
              />
            )}
          </Card>
        </Grid>
      </Grid>

      <DateAndTimePopover
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        startDate={startDate}
        endDate={endDate}
        handleDateChange={handleDateChange}
        timeRange={timeRange}
        setTimeRange={(timeRange) =>
          setTimeRange(
            Array.isArray(timeRange) ? timeRange : [timeRange, timeRange],
          )
        }
        handleDateSubmit={handleDateSubmit}
      />
    </Box>
  );
};

export default MeterCommandReport;
